import { useState } from 'react'
import { CourseJsonLd, FAQPageJsonLd, NextSeo } from 'next-seo'
import { Trans } from '@lingui/macro'

import Footer from '@components/common/footer'
import Navbar from '@components/common/navbar'
import CodingCertificate from '@components/home/coding-certificate'
import CodingalSubscription from '@components/home/codingal-subscription'
import Teachers, { StatisticsProps } from '@components/home/teachers'
import CourseCard, { Course } from '@components/courses/course-card'
import VideoModal from '@components/courses/video-modal'
import CoursesFAQ from '@components/courses/courses-faq'
import VideoTestimonial from '@components/locale-pages/home/video-testimonial'
import TrustedByParents from '@components/teacher-lp/trusted-by-parents'
import FirstFold from '@components/math-class/first-fold'
import SeePersonalizedLessons from '@components/math-class/see-personalized-lessons'
import LookingForMathHelp from '@components/math-class/looking-for-math-help'
import EveryClassCurriculum from '@components/math-class/every-class-curriculum'
import MorePractice from '@components/math-class/more-practice'
import LpaarMethod from '@components/math-class/lpaar-method'
import FoundationsFirst from '@components/math-class/foundations-first'
import InteractiveMathWorksheets from '@components/math-class/interactive-math-worksheets'
import TakeAPeek from '@components/math-class/take-a-peek'
import { defineMessage } from '@lingui/macro'
import { formatSeoConfig } from '@lib/seo'
import { VIDEO_TESTIMONIAL } from '@lib/data/summer-coding-camp'
import { MATH_FAQ_SECTIONS } from '@lib/data/math-faq'
import { MATH_COURSES } from '@lib/data/math-courses'
import MoneyBackGuarantee from '@components/home/guarantee'
import TrustpilotReview from '@components/locale-pages/home/trustpilot-review'
import AboutTeachers from '@components/about-us/about-teachers'
import { FastlyImage } from '@components/common/image'

const STATISTICS: StatisticsProps[] = [
  {
    title: '🎓 1000+',
    description: defineMessage({
      message: `Teachers with Bachelor's and Master's degrees in mathematics`,
    }),
  },
  {
    title: '👨‍💻 7+',
    description: defineMessage({
      message: 'Years average teaching experience',
    }),
  },
  {
    title: '👩 91%',
    description: defineMessage({
      message: 'Female experienced teachers',
    }),
  },
  {
    title: '🗣️ 15+',
    description: defineMessage({
      message: "Teaching accents to match your child's comfort and clarity",
    }),
  },
]

const DASHBOARD_FEATURES = [
  {
    title: 'Personalized dashboard',
    description:
      'Access lessons, assignments, reports, and learning updates through one dashboard.',
  },
  {
    title: 'Track your lessons',
    description:
      'View completed and upcoming modules, along with lessons, projects, and quizzes, all in one place.',
  },
  {
    title: '24/7 AI Cody tutor',
    description:
      'Get instant help with doubts, concepts, practice questions, and coding challenges anytime.',
  },
  {
    title: 'Track your progress',
    description:
      'Monitor course completion, skills developed, milestones, and overall learning progress.',
  },
  {
    title: '24/7 help and support',
    description:
      'Get quick assistance anytime for classes, schedules, payments, and technical queries.',
  },
]

const MATH_FEATURES = [
  <Trans key={1}>
    A <strong>handpicked tutor matched to your child&apos;s curriculum</strong>,
    pace, and accent.
  </Trans>,
  <Trans key={2}>
    <strong>Two free monthly doubt sessions</strong>, live 1:1 with their tutor.
  </Trans>,
  <Trans key={3}>
    <strong>Unlimited AI Tutor access</strong>, available anytime your child
    needs it.
  </Trans>,
  <Trans key={4}>
    A <strong>dedicated support team available 24x7</strong> for any questions.
  </Trans>,
  <Trans key={5}>
    <strong>Lifetime access</strong> to all class recordings and worksheets.
  </Trans>,
  <Trans key={6}>
    A{' '}
    <strong>
      <u>STEM.org accredited certificate</u>
    </strong>{' '}
    earned at every milestone.
  </Trans>,
  <Trans key={7}>
    <strong>Worksheets and quizzes</strong> after every class to reinforce the
    learning.
  </Trans>,
  <Trans key={8}>
    <strong>Monthly progress reports</strong> and regular parent-teacher
    meetings to keep you in the loop.
  </Trans>,
  <Trans key={9}>
    <strong>Flexible payment options</strong> to suit your budget.
  </Trans>,
  <Trans key={10}>
    A <strong>100% money-back guarantee</strong> if the first class isn&apos;t
    the right fit.
  </Trans>,
  <Trans key={11}>
    A <strong>personalized dashboard</strong> to track progress, access
    recordings, and review worksheets.
  </Trans>,
]

const urlQuery = '?course=math-trial-class'

export const MathCourse = formatSeoConfig({
  title: 'Online Math Classes for Kids | Live Math Courses',
  description:
    "Live 1:1 online math classes for kids, personalised to your child's school curriculum. CBSE, GCSE, IB, Common Core & more. 135 countries. Book a free trial.",
  url: 'https://www.codingal.com/courses/math',
  metaImage: 'https://www.codingal.com/images/courses/math/og-image-v2.png',
})

const MathClass = () => {
  const mathCourses = MATH_COURSES
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false)
  const [selectedVideo, setSelectedVideo] = useState<{
    url: string
    title: string
  } | null>(null)

  const handleVideoPlay = (course: Course) => {
    if (course.introVideoUrl) {
      setSelectedVideo({
        url: course.introVideoUrl,
        title: course.title,
      })
      setIsVideoModalOpen(true)
    }
  }

  const scrollToCourses = () => {
    document
      .getElementById('math-courses')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const scrollToPricing = () => {
    document
      .getElementById('pricing')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <>
      <NextSeo {...MathCourse} />
      <CourseJsonLd
        courseName="Online Math Classes for Kids and Teens"
        description="Live 1:1 online math classes for kids and teens in grades 1-12. Aligned with school boards including Common Core, GCSE, IB, NCERT, CBSE and 125+ more. Build strong math foundations through interactive lessons with expert tutors."
        provider={{
          '@type': 'Organization',
          name: 'Codingal',
        }}
        offers={{
          '@type': 'Offer',
          category: 'Paid',
        }}
        hasCourseInstance={{
          '@type': 'CourseInstance',
          courseMode: 'Online',
          courseWorkload: 'PT48H',
        }}
      />
      <FAQPageJsonLd
        mainEntity={MATH_FAQ_SECTIONS.flatMap(({ items }) =>
          items.map(({ question, answerText }) => ({
            acceptedAnswerText: answerText,
            questionName: question,
          }))
        )}
      />

      <Navbar tryFreeClassLink={`/register/${urlQuery}`} />

      <FirstFold />

      <SeePersonalizedLessons urlQuery={urlQuery} />

      <LookingForMathHelp urlQuery={urlQuery} />

      <EveryClassCurriculum urlQuery={urlQuery} />

      <MorePractice urlQuery={urlQuery} />

      {/* Online Math courses grid */}
      <section id="math-courses" className="bg-white py-12 lg:py-16">
        <div className="container mx-auto px-5">
          <h2 className="text-2xl lg:text-4xl font-600 leading-tight mb-8 text-center text-grey-900 max-w-4xl mx-auto">
            Online Math courses for kids, grade 1 to grade 12
          </h2>
          {mathCourses.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 space-y-2">
              <p className="text-xl font-600 text-grey-700">
                No math courses available right now.
              </p>
              <p className="text-grey-700">Check back soon for new courses.</p>
            </div>
          ) : (
            <div className="space-y-6 mx-auto">
              {mathCourses.map((course) => (
                <CourseCard
                  key={course.id}
                  course={course}
                  onVideoPlay={handleVideoPlay}
                  onViewPricing={scrollToPricing}
                />
              ))}
            </div>
          )}
        </div>
      </section>
      <section className="bg-orange-100 py-12 lg:py-16">
        <div className="container mx-auto px-5">
          <h2 className="text-2xl lg:text-4xl font-600 leading-tight mb-4 text-center text-grey-900 max-w-4xl mx-auto">
            A dashboard your child loves. Progress you can trust.
          </h2>
          <p className="text-lg text-grey max-w-4xl mx-auto text-center">
            From a 24/7 AI tutor in Cody to live progress reports and friendly
            leaderboards, your child&apos;s dashboard keeps them engaged while
            giving you a transparent, real-time view of every win along the way.
          </p>
          <div className="hidden lg:block">
            <FastlyImage
              src="/images/courses/math/dashboard.png"
              alt="Dashboard"
              className="mx-auto mt-16 -mb-8 relative pl-10"
              width={1176}
              height={620}
            />
          </div>
          <ul className="block lg:hidden max-w-2xl mx-auto mt-16 space-y-6">
            {DASHBOARD_FEATURES.map((feature) => (
              <li key={feature.title} className="flex gap-3">
                <span className="mt-2 h-2 w-2 rounded-full bg-grey-900 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-600 text-grey-900">
                    {feature.title}
                  </h3>
                  <p>{feature.description}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>
      {/* Pricing plans */}
      <section id="pricing">
        <CodingalSubscription
          urlQuery={urlQuery}
          subject="Math"
          features={MATH_FEATURES}
          ctaSize="md"
        />
      </section>

      <MoneyBackGuarantee urlQuery={urlQuery} ctaSize="md" />

      <LpaarMethod urlQuery={urlQuery} />

      <FoundationsFirst
        urlQuery={urlQuery}
        onViewCurriculum={scrollToCourses}
      />

      <InteractiveMathWorksheets urlQuery={urlQuery} />

      <TakeAPeek urlQuery={urlQuery} />
      <div className="bg-blue-50 bg-opacity-25">
        <Teachers
          isShowBecomeTeacher={false}
          title="Hand-picked, vetted, and matched to your child's board, pace, and accent."
          description="Every teacher on Codingal goes through a rigorous selection process covering subject expertise, teaching methodology, and communication clarity before they're assigned to a single student. Your child is then matched to a tutor based on their curriculum, learning pace, and the accent they're most comfortable learning in, so the very first class already feels like the right fit."
          urlQuery={urlQuery}
          statistics={STATISTICS}
          imagePath="/images/courses/math/teachers-v2"
          ctaSize="md"
        />
      </div>
      <AboutTeachers
        urlQuery={urlQuery}
        ctaSize="md"
        title="Real stories from our math teachers"
        subject="math"
        description="Every child learns differently. Our teachers combine patience, personal attention, and real-world methods to help students move from confusion and fear to confidence and independent problem-solving."
      />
      {/* Math completion certificates */}

      <TrustedByParents ctaSize="md" />
      <CodingCertificate
        title={
          <>
            Unlock your skills and earn{' '}
            <span className="yellow-underline">
              Math completion certifications
            </span>
          </>
        }
        description={
          "In this specialized math course, learners in grades 1 to 12 earn certificates that celebrate their achievements while unlocking special badges and leaderboard rankings, making math for kids a fun and rewarding experience. Students can earn the Rising Math Star, Math Champion, Math Prodigy, and Math Grandmaster Certificates. These certifications are easy to download and share, fully aligned with U.S. Common Core standards, and accredited by STEM.org. They can be added to your child's learning portfolio for sharing with the world and on social media."
        }
      />

      <div className="bg-blue-50 bg-opacity-25">
        <VideoTestimonial
          title="Hear from our students how our math courses changed their lives"
          video_testimonial={VIDEO_TESTIMONIAL}
          subject="math"
        />
        <TrustpilotReview urlQuery={urlQuery} ctaSize="md" />
      </div>
      <CoursesFAQ sections={MATH_FAQ_SECTIONS} />
      <a href={`/register/${urlQuery ?? ''}`}>
        <FastlyImage
          src="/images/courses/math/math-banner.png"
          alt="Footer banner"
          className="mx-auto mt-16 -mb-8 relative pl-10"
          width={1033}
          height={266}
        />
      </a>
      <Footer />

      <VideoModal
        isOpen={isVideoModalOpen}
        setIsOpen={setIsVideoModalOpen}
        videoUrl={selectedVideo?.url ?? null}
        title={selectedVideo?.title ?? ''}
      />
    </>
  )
}

// TODO: Remove once getInitialProps is removed from _app.tsx
export const getStaticProps = async () => {
  return {
    props: {},
  }
}

export default MathClass
